import {
  type AppMessage,
  type CapFilter,
  type CapKind,
  type GrantedCap,
  type Node,
  ProtocolError,
  readAppMessage,
  sendShellMessage,
  type WindowInfo,
} from "@gelatos/protocol";

export type AppStatus = "starting" | "running" | "crashed";

export type AppState = {
  status: AppStatus;
  tree: Node | null;
  title: string | null;
  error: string | null;
  /** Mount → first render, in ms. The §9 budget is 150ms. */
  firstRenderMs: number | null;
};

export type LogLevel = "debug" | "info" | "warn" | "error";

/**
 * Where an app's code lives, as far as the shell is concerned: a module URL and
 * nothing else. No handle, no closure, no shared object — because under
 * `IframeHost` (§4.4, phase 6) there is nothing else to share.
 */
export type AppSource =
  /** A module the browser loads into an isolate of its own. */
  | { kind: "module"; url: string }
  /**
   * An app that runs somewhere else entirely — a process in a Sprite, reached
   * over a WebSocket (§7 phase 5). Still just a name and an entry point: the
   * shell holds no handle on it either way.
   */
  | { kind: "remote"; entry: string; runtime?: string };

export type CapabilityRequest = {
  id: string;
  kind: CapKind;
  filter: CapFilter | undefined;
};

export type AppHostOptions = {
  window: WindowInfo;
  source: AppSource;
  onState: (state: AppState) => void;
  onLog?: (level: LogLevel, msg: string) => void;
  /**
   * The capabilities to mount the app with. §5.3: exactly one, a `collection`
   * scoped to the app's own private store. Called once, at mount.
   */
  capabilities?: () => GrantedCap[];
  /** Route `requestCapability` to the powerbox (§5.4). */
  onRequest?: (request: CapabilityRequest) => Promise<GrantedCap | null>;
};

/**
 * The only way the shell may reach an app (§4.4).
 *
 * Two implementations: `WorkerHost`, used through phase 5, and `IframeHost` in
 * phase 6. Nothing outside a host implementation may assume an app is
 * same-origin, shares memory, or is reachable by anything but its port — every
 * such shortcut works today and breaks the moment apps move to their own
 * origins.
 */
export interface AppHost {
  readonly window: WindowInfo;
  readonly state: AppState;
  start(): void;
  /** Deliver a UI event from a rendered node back to the app (§5.2). */
  emit(key: string, event: string, payload?: unknown): void;
  /** Hand over a capability the app did not ask for — a drop, typically. */
  grant(capability: GrantedCap): void;
  /** Take the app's world away without warning. */
  kill(reason?: string): void;
  /** Orderly shutdown: tell the app, then take its world away. */
  dispose(): void;
}

export type AppHostFactory = (options: AppHostOptions) => AppHost;

const INITIAL: AppState = {
  status: "starting",
  tree: null,
  title: null,
  error: null,
  firstRenderMs: null,
};

/**
 * Everything about hosting an app that does not depend on what kind of isolate
 * it runs in: the mount handshake, decoding, state, and revocation.
 *
 * A subclass supplies only the transport — create an isolate, hand it exactly
 * one `MessagePort`, and destroy it on request. That is the whole difference
 * between a Worker and a cross-origin iframe, which is the point.
 */
export abstract class PortAppHost implements AppHost {
  readonly window: WindowInfo;
  protected readonly source: AppSource;

  #port: MessagePort | null = null;
  #state: AppState = INITIAL;
  #startedAt = 0;
  #disposed = false;
  readonly #options: AppHostOptions;

  constructor(options: AppHostOptions) {
    this.#options = options;
    this.window = options.window;
    this.source = options.source;
  }

  get state(): AppState {
    return this.#state;
  }

  start(): void {
    if (this.#port !== null || this.#disposed) return;
    this.#startedAt = performance.now();

    let port: MessagePort;
    try {
      port = this.connect();
    } catch (error) {
      this.fail(`could not start app: ${describe(error)}`);
      return;
    }

    this.#port = port;
    port.onmessage = (event) => this.#receive(event);
    port.onmessageerror = () => this.fail("app sent a value that could not be deserialised");
    port.start();

    sendShellMessage(port, {
      t: "mount",
      window: this.window,
      capabilities: this.#options.capabilities?.() ?? [],
    });
  }

  emit(key: string, event: string, payload?: unknown): void {
    const port = this.#port;
    if (port === null || this.#state.status === "crashed") return;
    try {
      sendShellMessage(
        port,
        payload === undefined ? { t: "event", key, event } : { t: "event", key, event, payload },
      );
    } catch (error) {
      this.#options.onLog?.("error", `dropped event ${key}/${event}: ${describe(error)}`);
    }
  }

  grant(capability: GrantedCap): void {
    const port = this.#port;
    if (port === null || this.#state.status === "crashed") return;
    sendShellMessage(port, { t: "capabilityGranted", id: capability.id, port: capability.port });
  }

  kill(reason = "app terminated"): void {
    this.fail(reason);
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    const port = this.#port;
    if (port !== null && this.#state.status !== "crashed") {
      try {
        sendShellMessage(port, { t: "unmount" });
      } catch {
        // The app is already gone; nothing to tell.
      }
    }
    this.#teardown();
  }

  /**
   * Bring the app into existence and return the port the shell talks on.
   *
   * A local host makes a `MessageChannel`, hands one end to an isolate and
   * returns the other. A bridged host returns a channel multiplexed over a
   * WebSocket. Everything above this line is identical either way, which is
   * what "the shell cannot tell local from remote" has to mean to be worth
   * anything.
   */
  protected abstract connect(): MessagePort;

  /** Destroy the isolate. Must be safe to call more than once. */
  protected abstract closeIsolate(): void;

  /** Report that the isolate died, or misbehaved badly enough to be ended. */
  protected fail(reason: string): void {
    if (this.#state.status === "crashed") return;
    this.#teardown();
    this.#update({ status: "crashed", error: reason });
  }

  #receive(event: MessageEvent): void {
    if (this.#disposed) return;
    let message: AppMessage;
    try {
      message = readAppMessage(event);
    } catch (error) {
      // A malformed message is a contract violation, not a shell problem.
      this.fail(
        error instanceof ProtocolError
          ? `protocol violation — ${error.message}`
          : `protocol violation — ${describe(error)}`,
      );
      return;
    }

    switch (message.t) {
      case "render":
        this.#update({
          status: "running",
          tree: message.tree,
          error: null,
          firstRenderMs:
            this.#state.firstRenderMs ?? Math.round(performance.now() - this.#startedAt),
        });
        return;
      case "title":
        this.#update({ title: message.title });
        return;
      case "log":
        this.#options.onLog?.(message.level, message.msg);
        return;
      case "requestCapability":
        void this.#broker(message.id, message.kind, message.filter);
        return;
    }
  }

  /**
   * Hand a request to the powerbox and answer with whatever the user chose.
   *
   * A refusal and a closed picker are the same message (§5.4): from the app's
   * side "you may not have this" and "the user declined to choose" are the same
   * fact, and there is nothing to be gained by telling them apart.
   */
  async #broker(id: string, kind: CapKind, filter: CapFilter | undefined): Promise<void> {
    let granted: GrantedCap | null = null;
    try {
      granted = (await this.#options.onRequest?.({ id, kind, filter })) ?? null;
    } catch (error) {
      this.#options.onLog?.("error", `capability request failed: ${describe(error)}`);
    }
    const port = this.#port;
    if (port === null || this.#disposed) return;
    if (granted === null) {
      sendShellMessage(port, { t: "capabilityDenied", id });
      return;
    }
    // The id the app asked under, not the one the broker minted: the app has to
    // be able to match the answer to its own question.
    sendShellMessage(port, { t: "capabilityGranted", id, port: granted.port });
  }

  #update(patch: Partial<AppState>): void {
    this.#state = { ...this.#state, ...patch };
    this.#options.onState(this.#state);
  }

  #teardown(): void {
    const port = this.#port;
    if (port !== null) {
      port.onmessage = null;
      port.close();
      this.#port = null;
    }
    this.closeIsolate();
  }
}

export function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
